import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, PlugZap, RefreshCw, Gift, Unplug } from "lucide-react";
import { toast } from "sonner";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { OpsSubnav } from "@/components/OpsSubnav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TenantLimitsOverrideDialog } from "@/components/admin/TenantLimitsOverrideDialog";

export const Route = createFileRoute("/admin/ops/mcp")({
  component: AdminMcpPage,
  head: () => ({ meta: [{ title: "IA conectada (MCP) — Admin — Structura" }] }),
});

interface McpTenant {
  id: string;
  slug: string;
  name: string;
  planId: string | null;
  enabled: boolean;
  creditsMonth: number | null;
  maxUsers: number | null;
  planUsedMonth: number;
  packBalance: number;
  remaining: number | null;
  creditsPeriod: number;
  callsPeriod: number;
  deniedPeriod: number;
  errorsPeriod: number;
  connectedUsers: number;
  lastCallAt: string | null;
  hasOverride: boolean;
}

interface McpCall {
  id: string;
  tenantId: string;
  tenantSlug: string | null;
  tenantName: string | null;
  userEmail: string | null;
  userName: string | null;
  client: string | null;
  tool: string;
  kind: string;
  credits: number;
  source: string;
  status: string;
  errorCode: string | null;
  durationMs: number | null;
  createdAt: string;
}

interface McpAdminData {
  days: number;
  kpis: { calls?: number; denied?: number; errors?: number; credits?: number; tenants?: number; users?: number; avg_ms?: number };
  tenants: McpTenant[];
  byTool: Array<{ tool: string; kind: string; calls: number; credits: number; errors: number }>;
  daily: Array<{ day: string; calls: number; credits: number }>;
  recent: McpCall[];
}

const STATUS_STYLE: Record<string, string> = {
  ok: "text-emerald-600 dark:text-emerald-400",
  pending: "text-muted-foreground",
  error: "text-red-600 dark:text-red-400",
  denied: "text-amber-600 dark:text-amber-400",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
}

function n(v: number | null | undefined): string {
  return v == null ? "∞" : Number(v).toLocaleString("es-AR");
}

function AdminMcpPage() {
  const [data, setData] = useState<McpAdminData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [tenantFilter, setTenantFilter] = useState<string | null>(null);
  const [overrideFor, setOverrideFor] = useState<McpTenant | null>(null);
  const [grantFor, setGrantFor] = useState<McpTenant | null>(null);
  const [grantCredits, setGrantCredits] = useState("500");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const q = new URLSearchParams({ days: String(days) });
    if (tenantFilter) q.set("tenantId", tenantFilter);
    void apiGetJson<McpAdminData>(`/api/admin/mcp?${q.toString()}`)
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days, tenantFilter]);
  useEffect(load, [load]);

  const maxDaily = useMemo(() => Math.max(1, ...(data?.daily ?? []).map((d) => d.calls)), [data]);
  const filteredTenant = data?.tenants.find((t) => t.id === tenantFilter) ?? null;

  const grant = async () => {
    if (!grantFor) return;
    setBusy(true);
    try {
      await apiSendJson("/api/admin/mcp", "POST", { action: "grant", tenantId: grantFor.id, credits: Number(grantCredits) });
      toast.success(`${grantCredits} créditos otorgados a ${grantFor.name}`);
      setGrantFor(null);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo otorgar");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (t: McpTenant) => {
    if (!window.confirm(`¿Desconectar todas las conexiones de IA de ${t.name}? El usuario va a tener que volver a conectar.`)) return;
    try {
      const r = await apiSendJson<{ revoked: number }>("/api/admin/mcp", "POST", { action: "revoke", tenantId: t.id });
      toast.success(`${r.revoked} conexiones revocadas`);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo revocar");
    }
  };

  const k = data?.kpis ?? {};
  return (
    <div className="space-y-6">
      <OpsSubnav hint="IA conectada (MCP): ChatGPT, Claude y Gemini trabajando sobre los datos de cada comercio. Créditos por llamada, cupo por plan, packs y registro de llamadas." />

      <div className="flex flex-wrap items-center gap-2">
        <PlugZap className="h-5 w-5 text-primary" />
        <h1 className="text-lg font-semibold">IA conectada (MCP)</h1>
        <div className="ml-auto flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)}>
              {d} días
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={load} aria-label="Actualizar">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Costo por llamada: lectura 1 crédito · consulta genérica 2 · escritura 3. Cupo mensual y usuarios por plan en{" "}
        <Link to="/admin" search={{ tab: "planes" }} className="underline">Planes</Link> (Créditos IA conectada / Usuarios IA conectada); packs en{" "}
        <Link to="/admin" search={{ tab: "packs" }} className="underline">Packs</Link> (tipo mcp).
      </p>

      {error ? <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{error}</p> : null}
      {loading && !data ? (
        <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : null}

      {data ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { label: "Llamadas", value: n(k.calls) },
              { label: "Créditos consumidos", value: n(k.credits) },
              { label: "Comercios activos", value: n(k.tenants) },
              { label: "Usuarios", value: n(k.users) },
              { label: "Rechazadas (cupo/plan)", value: n(k.denied) },
              { label: "Errores", value: n(k.errors) },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border bg-card p-3">
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className="mt-1 text-xl font-semibold tabular-nums">{s.value}</p>
              </div>
            ))}
          </div>

          <section className="rounded-xl border bg-card p-4">
            <h2 className="text-sm font-semibold">Llamadas por día{filteredTenant ? ` — ${filteredTenant.name}` : ""}</h2>
            {data.daily.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Sin llamadas en el período</p>
            ) : (
              <div className="mt-3 flex h-28 items-end gap-1" role="img" aria-label="Llamadas por día">
                {data.daily.map((d) => (
                  <div key={d.day} className="group flex flex-1 flex-col items-center justify-end" title={`${d.day}: ${d.calls} llamadas, ${d.credits} créditos`}>
                    <div className="w-full rounded-t bg-primary/70 group-hover:bg-primary" style={{ height: `${Math.max(4, (d.calls / maxDaily) * 100)}%` }} />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">Comercios</h2>
              {tenantFilter ? (
                <Button size="sm" variant="ghost" onClick={() => setTenantFilter(null)}>Ver todos</Button>
              ) : null}
            </div>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Comercio</th>
                    <th className="px-3 py-2 text-left font-medium">Plan</th>
                    <th className="px-3 py-2 text-right font-medium">Cupo/mes</th>
                    <th className="px-3 py-2 text-right font-medium">Usado mes</th>
                    <th className="px-3 py-2 text-right font-medium">Packs</th>
                    <th className="px-3 py-2 text-right font-medium">Disponible</th>
                    <th className="px-3 py-2 text-right font-medium">Llamadas</th>
                    <th className="px-3 py-2 text-right font-medium">Rechazos</th>
                    <th className="px-3 py-2 text-right font-medium">Usuarios</th>
                    <th className="px-3 py-2 text-left font-medium">Última</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {data.tenants.map((t) => (
                    <tr key={t.id} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="px-3 py-2">
                        <button type="button" className="text-left font-medium hover:underline" onClick={() => setTenantFilter(t.id)}>
                          {t.name || t.slug}
                        </button>
                        <div className="text-[11px] text-muted-foreground">{t.slug}{t.hasOverride ? " · override" : ""}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{t.planId ?? "—"}{t.enabled ? "" : " (sin MCP)"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.creditsMonth)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.planUsedMonth)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.packBalance)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.remaining)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{n(t.callsPeriod)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${t.deniedPeriod ? "text-amber-600" : "text-muted-foreground"}`}>{t.deniedPeriod}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{t.connectedUsers}/{n(t.maxUsers)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(t.lastCallAt)}</td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => setGrantFor(t)} title="Otorgar créditos"><Gift className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="outline" onClick={() => setOverrideFor(t)}>Límites</Button>
                          <Button size="sm" variant="ghost" onClick={() => void revoke(t)} title="Desconectar"><Unplug className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {data.tenants.length === 0 ? (
                    <tr><td colSpan={11} className="py-8 text-center text-muted-foreground">Ningún comercio conectó su IA todavía</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>

          {grantFor ? (
            <div className="flex flex-wrap items-end gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3">
              <div className="text-sm">Otorgar créditos a <strong>{grantFor.name}</strong> (vencen en 90 días)</div>
              <Input className="h-8 w-28" type="number" min={1} value={grantCredits} onChange={(e) => setGrantCredits(e.target.value)} />
              <Button size="sm" disabled={busy} onClick={() => void grant()}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Otorgar"}</Button>
              <Button size="sm" variant="ghost" onClick={() => setGrantFor(null)}>Cancelar</Button>
            </div>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
            <section className="space-y-2">
              <h2 className="text-sm font-semibold">Herramientas más usadas</h2>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
                    <tr><th className="px-3 py-2 text-left font-medium">Tool</th><th className="px-3 py-2 text-right font-medium">Llamadas</th><th className="px-3 py-2 text-right font-medium">Créditos</th></tr>
                  </thead>
                  <tbody>
                    {data.byTool.map((r) => (
                      <tr key={r.tool + r.kind} className="border-b last:border-0">
                        <td className="px-3 py-1.5 font-mono text-xs">{r.tool.replace(/^structura\./, "")}<span className="ml-1 text-muted-foreground">({r.kind})</span></td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{r.calls}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{r.credits}</td>
                      </tr>
                    ))}
                    {data.byTool.length === 0 ? <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">—</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="space-y-2">
              <h2 className="text-sm font-semibold">Registro de llamadas{filteredTenant ? ` — ${filteredTenant.name}` : ""}</h2>
              <div className="max-h-[480px] overflow-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b bg-muted/80 text-xs text-muted-foreground backdrop-blur">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Fecha</th>
                      <th className="px-3 py-2 text-left font-medium">Comercio / usuario</th>
                      <th className="px-3 py-2 text-left font-medium">Tool</th>
                      <th className="px-3 py-2 text-right font-medium">Créd.</th>
                      <th className="px-3 py-2 text-left font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((c) => (
                      <tr key={c.id} className="border-b last:border-0">
                        <td className="whitespace-nowrap px-3 py-1.5 text-xs text-muted-foreground">{fmtDate(c.createdAt)}</td>
                        <td className="px-3 py-1.5 text-xs">
                          <div className="font-medium">{c.tenantName ?? c.tenantSlug ?? c.tenantId}</div>
                          <div className="text-muted-foreground">{c.userEmail ?? "—"}{c.client ? ` · ${c.client}` : ""}</div>
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs">{c.tool.replace(/^structura\./, "")}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{c.credits}{c.source === "pack" ? " (pack)" : ""}</td>
                        <td className={`px-3 py-1.5 text-xs ${STATUS_STYLE[c.status] ?? ""}`}>
                          {c.status}{c.errorCode ? ` · ${c.errorCode}` : ""}{c.durationMs != null ? ` · ${c.durationMs} ms` : ""}
                        </td>
                      </tr>
                    ))}
                    {data.recent.length === 0 ? <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin llamadas</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </>
      ) : null}

      <TenantLimitsOverrideDialog
        tenantId={overrideFor?.id ?? null}
        tenantName={overrideFor?.name}
        open={overrideFor != null}
        onOpenChange={(open) => { if (!open) setOverrideFor(null); }}
        onSaved={load}
      />
    </div>
  );
}
